<template>
  <div class="shrinkage-table">
    <div v-if="isNew" class="row row-edit">
      <EditShrinkageRow
        :isNew="true"
        :shrinkage="editing"
        :teamOptions="teamOptions"
        :configuration="configuration"
        :categoryOptions="categoryOptions"
        :month="month"
        @cancel="cancel"
        @save="save($event)"
      />
    </div>
    <template v-for="(row, index) in rowsInMonth">
      <div class="row" :class="{ 'row-edit': editing === row }" :key="index">
        <template v-if="editing !== row">
          <div class="row-data" data-testid="shrinkage-table-existing">
            <div>{{ formatDate(row.startDate) }}</div>
            <div>{{ formatDate(row.endDate) }}</div>
            <div>{{ formatPercentage(row.percentage) }}</div>
            <div>{{ formatTeams(row.teams) }}</div>
            <div>{{ formatCategories(row.categories) }}</div>
          </div>
          <div class="row-actions">
            <AlButton
              @click="edit(row)"
              v-if="!isNew"
              data-testid="shrinkage-table-edit-existing"
              id="shrinkage-table-edit-existing"
              :disabled="!editingAllowed"
              :tooltip="
                editingAllowed
                  ? ''
                  : 'New adjustments cannot be made to months in the past'
              "
              ><i class="fa fa-edit"
            /></AlButton>
            <AlButton
              @click="remove(row)"
              v-if="!isNew"
              variant="warning"
              data-testid="shrinkage-table-delete-existing"
              id="shrinkage-table-delete-existing"
              ><i class="fa fa-trash-alt"
            /></AlButton>
          </div>
        </template>
        <template v-if="editing === row">
          <EditShrinkageRow
            :isNew="false"
            :shrinkage="editing"
            :teamOptions="teamOptions"
            :configuration="configuration"
            :categoryOptions="categoryOptions"
            :month="month"
            @cancel="cancel"
            @save="save($event, index)"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { format, isSameMonth, parseISO } from "date-fns";
import {
  RosterConfiguration,
  Translator,
  TaskCategory,
  Team,
} from "@agyletime/wfm-vue-common";
import { Al2Button as AlButton } from "@playvox/almendra-components";
import EditShrinkageRow from "./EditShrinkageRow.vue";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "ShrinkageTable",
  mixins: [Translator],
  components: {
    EditShrinkageRow,
    AlButton,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    month: {
      type: String,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    editingAllowed: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    isNew() {
      if (this.isNew) {
        this.editing = {};
      } else {
        this.editing = null;
      }
    },
    rows() {
      this.hydrateCategories();
      this.hydrateTeams();
    },
    $route() {
      this.editing = null;
    },
  },
  created() {
    this.getCategoryOptions();
    this.getTeamOptions();
  },
  data() {
    return {
      editing: null,
      categoryOptions: [],
      teamOptions: [],
    };
  },
  computed: {
    rowsInMonth() {
      return this.rows
        .map((row, index) => ({
          ...row,
          index,
        }))
        .filter((row) => {
          const dateOne = utcToZonedTime(row.startDate, "UTC");
          const dateTwo = utcToZonedTime(new Date(this.month), "UTC");
          return isSameMonth(dateOne, dateTwo);
        });
    },
  },
  methods: {
    formatDate(value) {
      let dateToFormat = utcToZonedTime(value, "UTC");
      if (typeof dateToFormat === "string") {
        dateToFormat = parseISO(value);
      }
      return format(dateToFormat, "yyyy-MM-dd");
    },
    formatPercentage(value) {
      return `${value}%`;
    },
    formatTeams(value = []) {
      if (value.length === 0) {
        return "All Teams";
      }
      return value.map((v) => v?.name).join(", ");
    },
    formatCategories(value = []) {
      if (value.length === 0) {
        return "All Shrinkage";
      }
      return value.map((v) => v?.name).join(", ");
    },
    edit(row) {
      this.editing = row;
    },
    remove(row) {
      const rows = [...this.rows];
      rows.splice(row.index, 1);
      this.$emit("save", rows);
    },
    cancel() {
      this.editing = null;
      this.$emit("cancel");
    },
    save(shrinkage, index) {
      const rows = [...this.rows];
      if (this.isNew) {
        this.$emit("save", [shrinkage, ...rows]);
      } else {
        rows.splice(index, 1, shrinkage);
        this.$emit("save", rows);
      }
      this.editing = null;
    },
    async getTeamOptions() {
      // Get the teams that are selected in the given rosters
      const rosterConfigs = await RosterConfiguration.getRosterConfigurations({
        fields: "teams,_id,name",
      });
      const selectedConfigs = rosterConfigs.filter((rc) => {
        return this.configuration.rosterConfigurationId.find(
          (rcid) => rcid._id == rc._id
        );
      });

      // Get unique team ids in the rosters
      const availableTeamIds = selectedConfigs.reduce((p, c) => {
        p.push(...c.teams.map((t) => t.id));
        return p;
      }, []);
      const uniqueTeamIdsSet = new Set(availableTeamIds);
      const allTeams = await Team.getTeams({ active: "Y" });

      this.teamOptions = allTeams.filter((t) => uniqueTeamIdsSet.has(t.id));

      this.hydrateTeams();
    },
    hydrateTeams() {
      this.rows.forEach((row) => {
        row.teams = row.teams.map((team) => {
          return (
            this.teamOptions.find((option) => option.id == team.id) || team
          );
        });
      });
    },
    async getCategoryOptions() {
      const allOptions = await TaskCategory.getTaskCategories();
      this.categoryOptions = allOptions.filter(
        (option) => option.type === "shrinkage"
      );
      this.hydrateCategories();
    },
    hydrateCategories() {
      this.rows.forEach((row) => {
        row.categories = row.categories.map((category) => {
          return (
            this.categoryOptions.find((option) => option.id == category.id) ||
            category
          );
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-shrinkage {
  max-width: 100%;
  width: 20rem;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.row {
  padding: 1rem;
  border-bottom: 1px solid var(--color-eerie-black-40);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;

  &:not(.row-edit):hover {
    background-color: var(--color-playvox-green-10);

    .row-actions > * {
      opacity: 1;
      pointer-events: all;
    }
  }
}

.row-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;

  & > * {
    width: 20%;
  }
}

.row-actions > * {
  margin-right: 0.5rem;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 899px) {
  .row-data {
    min-width: 100%;
  }
  .row-data > * {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 650px) {
  .row-data > * {
    min-width: 100%;
  }
  .row-actions > * {
    opacity: 1;
    pointer-events: all;
  }
}
</style>

<style>
.dr-input--sm.dr-input--has-icon .dr-input__append {
  padding-top: 3px;
}

.panel-header {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}
</style>
